export type UrlFilterValues = Record<string, string[]>;

export function useUrlFilters(keys: () => readonly string[]) {
  const route = useRoute();
  const router = useRouter();

  const applied = computed<UrlFilterValues>(() => {
    const result: UrlFilterValues = {};
    for (const key of keys()) {
      const raw = route.query[key];
      const value = Array.isArray(raw)
        ? (raw.filter(Boolean) as string[])
        : raw
          ? String(raw).split(",").filter(Boolean)
          : [];
      if (value.length) result[key] = value;
    }
    return result;
  });

  const draft = ref<UrlFilterValues>(clone(applied.value));

  function clone(v: UrlFilterValues): UrlFilterValues {
    const out: UrlFilterValues = {};
    for (const k of Object.keys(v)) out[k] = [...v[k]!];
    return out;
  }

  function commitToUrl(values: UrlFilterValues) {
    const query = { ...route.query };
    for (const key of keys()) {
      const arr = values[key];
      if (arr && arr.length) query[key] = arr.join(",");
      else delete query[key];
    }
    router.push({ query });
  }

  function syncDraftFromApplied() {
    draft.value = clone(applied.value);
  }

  function apply() {
    commitToUrl(draft.value);
  }

  function clearDraft() {
    draft.value = {};
  }

  function clearAppliedKey(key: string, value?: string) {
    const next = clone(applied.value);
    if (value === undefined) {
      delete next[key];
    } else if (next[key]) {
      next[key] = next[key].filter((v) => v !== value);
      if (!next[key].length) delete next[key];
    }
    commitToUrl(next);
  }

  function toggleDraft(key: string, value: string) {
    const arr = draft.value[key] ?? [];
    const next = { ...draft.value };
    if (arr.includes(value)) {
      const filtered = arr.filter((v) => v !== value);
      if (filtered.length) next[key] = filtered;
      else delete next[key];
    } else {
      next[key] = [...arr, value];
    }
    draft.value = next;
  }

  function setDraftValues(key: string, values: string[]) {
    const next = { ...draft.value };
    const unique = Array.from(new Set(values));
    if (unique.length) next[key] = unique;
    else delete next[key];
    draft.value = next;
  }

  function isDraftSelected(key: string, value: string) {
    return draft.value[key]?.includes(value) ?? false;
  }

  return {
    applied,
    draft,
    apply,
    clearDraft,
    clearAppliedKey,
    syncDraftFromApplied,
    toggleDraft,
    setDraftValues,
    isDraftSelected,
  };
}
